import dayjs, { type Dayjs } from 'dayjs'
import JsBarcode from 'jsbarcode'
import type { PrinterConfig } from '@/utils/qzTray/qzTrayClient'
import {
  COMMAND_PREFIX,
  JP_LABEL_SIZE_SINCE,
  LABEL_HEIGHT_DEFAULT,
  LABEL_HEIGHT_JP,
  LABEL_PRINTER_BASE,
  MULTI_CONSOLIDATION_MESSAGE,
  ORDER_OPTS_MAX,
  ORDER_OPTS_PER_LINE,
  SCAN_COMMANDS,
} from '../consts'
import type { InboundOrderLabel, InboundScanRow, ScanCommandType } from '../queries/types'

/**
 * 입고 라벨 HTML — 1.0 `GLTemplate.js` 이식.
 *
 * 1.0 은 숨긴 `<GLTemplate>` 을 그려 두고 인쇄 때 `.label_space` 의 innerHTML 을 qz 로 보냈다. 2.0 은 화면에 미리보기를 두지 않으므로
 * 같은 마크업·같은 인라인 스타일·같은 분기를 문자열로 바로 만든다(반품 2.0 `RgrLabelTemplate` 과 같은 방식).
 * 바코드는 1.0 `react-barcode` 대신 레포에 있는 `jsbarcode` 로 그린 SVG 다(CODE128, 값 표시 없음 — 1.0 과 같은 옵션).
 * 분기 조건은 1.0 식을 그대로 옮겼다 — 1.0 라벨 회귀 테스트(GL09.label*.test.js)를 `inboundLabel.test.ts` 로 옮겨 같은 기대값을 본다.
 */

/** 스캔 판정(1.0 redux `result`) — triggered = 실패(미매칭·HTTP 오류·빈 응답), message = 서버 안내. */
export interface LabelMissMatch {
  triggered: boolean
  message: string
}

export interface InboundLabelInput {
  /** 입력 원문(1.0 `newBarcode`) — 송장 또는 특수 바코드 `!@#…$%^` */
  barcode: string
  /** 스캔 결과 첫 행. 실패면 없다(1.0 GL09_HEAD_DATA 가 지워진다). */
  result?: InboundScanRow
  missMatch: LabelMissMatch
  /** 합포 주문(1.0 `orderList`). 실패 라벨에는 찍지 않는다(LOGISPRD-14966). */
  orderList: InboundOrderLabel[]
  /** 라벨 날짜 — 1.0 은 `YYYY-M-D`(0 채움 없음). */
  date: string
  /** 로고 이미지 src — 기본 QR, 티몰(TS) 주문이면 티몰 이미지. */
  images: { qr: string; tmall: string }
}

/** 1.0 라벨 정보 줄(`labelInfo`)과 접두어(`keyMap`). */
const LABEL_INFO_KEYS = ['SVC_TYPE', 'TOTAL_QTY', 'INB_TRS_INVOICE', 'OUB_PDATE'] as const
const KEY_PREFIX: Partial<Record<(typeof LABEL_INFO_KEYS)[number], string>> = {
  TOTAL_QTY: '수량',
  INB_TRS_INVOICE: '송장번호',
  OUB_PDATE: '출고요청일',
}

const TREBUCHET = 'Trebuchet MS'

const escapeHtml = (value: string): string =>
  value.replaceAll(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)

/** 인라인 스타일 — React style 객체처럼 빈 값은 빼고 camelCase 를 CSS 이름으로 바꾼다. */
const css = (style: Record<string, string>): string =>
  Object.entries(style)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => `${k.replaceAll(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${v}`)
    .join(';')

const el = (tag: string, style: Record<string, string> | null, inner: string, attrs = ''): string =>
  `<${tag}${attrs}${style ? ` style="${escapeHtml(css(style))}"` : ''}>${inner}</${tag}>`

/** 1.0 날짜 표기(`year + '-' + month + '-' + date`). */
export const toLabelDate = (now: Date = new Date()): string => `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`

/** 일본 규격 라벨인가 — 1.0 GLTemplate `isJPSize` 그대로(24/05/07 PM 12:30 부터 true). */
export const isJPSize = (now: Dayjs = dayjs()): boolean => now.isAfter(JP_LABEL_SIZE_SINCE)

/** 라벨 프린터 설정 — 폭 10cm, 높이 일본 규격 15 아니면 8, 일본 규격이면 reverse-landscape(1.0 printStart). */
export const labelPrinterConfig = (now: Dayjs = dayjs()): PrinterConfig => {
  const jp = isJPSize(now)
  return {
    ...LABEL_PRINTER_BASE,
    height: jp ? LABEL_HEIGHT_JP : LABEL_HEIGHT_DEFAULT,
    ...(jp ? { orientation: 'reverse-landscape' as const } : {}),
  }
}

/** 명령 → 특수 바코드 토큰(스캔 파싱과 같은 표 `SCAN_COMMANDS`). */
const commandToken = (command: ScanCommandType): string => {
  const token = SCAN_COMMANDS.find(([, c]) => c === command)?.[0]
  if (!token) throw new Error(`특수 바코드 토큰이 없습니다: ${command}`)
  return token
}

/**
 * 몇 장 찍나 — 1.0 GLTemplate 의 두 effect(결과 변경·실패 변경)를 한 식으로 합쳤다.
 *  실패: 메시지가 있고 '처리 불가'가 아니면 1장 · 성공: RESET 0장, QTY·임의 합포건 1장, 그 밖은 PRINT_QTY(0 이면 0, 없으면 1).
 */
export const labelPrintCount = ({ barcode, result, missMatch }: Pick<InboundLabelInput, 'barcode' | 'result' | 'missMatch'>): number => {
  if (missMatch.triggered) return missMatch.message && !missMatch.message.includes('처리 불가') ? 1 : 0
  if (!barcode.length || !result) return 0
  if (barcode.includes(commandToken('RESET'))) return 0
  if (barcode.includes(commandToken('QUANTITY_MISMATCH')) || missMatch.message.includes(MULTI_CONSOLIDATION_MESSAGE)) return 1
  if (result.PRINT_QTY >= 1) return result.PRINT_QTY
  if (result.PRINT_QTY === 0) return 0
  return 1
}

/** 보관구역 칸 — 1.0 `editResult`(괄호 줄바꿈·이형·위험물·미주 처리). */
export const formatStorageZone = (result: InboundScanRow | undefined): string => {
  if (!result) return ''
  const value = result.INV_TYPE
  if (value.includes('(')) {
    const broken = value.replace('(', '(\n ').replace(')', '\n)')
    if (result.IS_VARIANT === 'Y') return `${broken}\n-\n\n\n이형\n`
    if (value.includes('미주')) return broken.replaceAll(/[^\w\s가-힣]/g, ' ').replaceAll(/(\s+)/g, '\n$1')
    return broken
  }
  if (result.DANGER === 'Y' && value !== '위험물') return `${value}\n-\n\n\n위험물\n`
  if (result.IS_VARIANT === 'Y') return `${value}\n-\n\n\n이형\n`
  if (value.includes('미주')) return value.replaceAll(/(\s+)/g, '\n$1')
  return value
}

const barcodeSvg = (value: string, width: number): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svg, value, { format: 'CODE128', width, height: 50, fontSize: 13, textAlign: 'center', textPosition: 'bottom', displayValue: false })
  } catch {
    return `<div style="color:red;font-size:12px;">잘못된 바코드: ${escapeHtml(value)}</div>`
  }
  svg.setAttribute('data-barcode-value', value)
  return svg.outerHTML
}

const orderBlock = (orderList: InboundOrderLabel[]): string => {
  const lineStyle = { lineHeight: '1.4', fontFamily: TREBUCHET, fontWeight: '900' }
  const shown = Math.min(orderList.length, ORDER_OPTS_MAX)
  const lines = Math.ceil(shown / ORDER_OPTS_PER_LINE)
  const optLines = Array.from({ length: lines }, (_, i) => {
    const text = orderList
      .slice(i * ORDER_OPTS_PER_LINE, Math.min(i * ORDER_OPTS_PER_LINE + ORDER_OPTS_PER_LINE, ORDER_OPTS_MAX))
      .map((o) => o.SPR_ORD_OPT_NO)
      .join(', ')
    const more = i === lines - 1 && orderList.length > ORDER_OPTS_MAX ? ` 외 ${orderList.length - ORDER_OPTS_MAX}건` : ''
    return el('div', null, escapeHtml(text + more))
  }).join('')
  return (
    el('div', lineStyle, `주문번호 : ${escapeHtml(orderList[0]?.SPR_ORD_NO ?? '')}`) +
    el('div', lineStyle, el('div', null, '주문일련번호 :') + optLines)
  )
}

/** 라벨 HTML — 1.0 GLTemplate 의 JSX 를 같은 순서·같은 조건으로 옮겼다. */
export const buildInboundLabelHtml = ({ barcode, result, missMatch, orderList, date, images }: InboundLabelInput): string => {
  const trig = missMatch.triggered
  const isCommand = barcode.includes(COMMAND_PREFIX)
  const isQty = barcode.includes(commandToken('QUANTITY_MISMATCH'))
  const isVariant = barcode.includes(commandToken('VARIANT'))
  const isReprint = barcode.includes(commandToken('REPRINT'))
  const isReset = barcode.includes(commandToken('RESET'))
  const isMulti = missMatch.message.includes(MULTI_CONSOLIDATION_MESSAGE)
  const normalLabel = !isCommand || isVariant || isReprint

  // 왼쪽 로고 칸 — 일반·이형·재출력 성공 라벨에만
  const qrArea =
    normalLabel && !trig && !isMulti
      ? el(
          'div',
          {
            width: '30%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingRight: '10px',
            marginRight: '10px',
            borderRight: '1px dashed black',
          },
          el(
            'div',
            { display: 'flex', justifyContent: 'center' },
            el(
              'div',
              {
                width: '90%',
                padding: '5px 5px',
                textAlign: 'center',
                fontSize: '13px',
                fontFamily: 'musinsa, Trebuchet MS',
                fontWeight: '900',
                color: 'black',
              },
              'MUSINSA',
            ),
          ) +
            `<img src="${escapeHtml(orderList.some((o) => o.TRS_COUNTRY === 'TS') ? images.tmall : images.qr)}" alt="musinsa" width="100%">` +
            el(
              'div',
              {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                fontFamily: TREBUCHET,
                fontSize: '8px',
                fontWeight: '900',
              },
              el('p', { lineHeight: '1.3', textAlign: 'center', marginBottom: '15px' }, 'Thank you for<br>shopping with us.'),
            ),
          ' class="qrArea"',
        )
      : ''

  // 큰 글자 칸 — 명령·실패·임의 합포건은 판정 제목, 그 밖은 보관구역
  const dateBlock = (value: string) =>
    el(
      'div',
      { width: '100%', textAlign: 'center', lineHeight: '1.4', fontFamily: TREBUCHET, fontSize: '15px' },
      `<p>${escapeHtml(date)}</p><p>${escapeHtml(value)}</p>`,
    )
  const headerCase = (!trig && isCommand && !isReprint && !isVariant) || trig || (!trig && isMulti)
  let head: string
  if (headerCase && isCommand) {
    const title = isQty ? '수량상이' : isVariant ? '이형' : '초기화'
    head = el(
      'div',
      {
        width: '150%',
        padding: '30px 10px 0 10px',
        marginBottom: '10px',
        textAlign: 'center',
        fontFamily: TREBUCHET,
        fontSize: '30px',
        fontWeight: '900',
      },
      title + dateBlock(trig ? barcode : result?.INB_TRS_INVOICE ?? ''),
      ' class="place"',
    )
  } else if (headerCase) {
    head = el(
      'div',
      {
        width: '150%',
        padding: trig ? '30px 10px 0 10px' : '70px 10px 70px 10px',
        marginBottom: '10px',
        textAlign: 'center',
        border: trig ? '' : '1px solid black',
        fontFamily: TREBUCHET,
        fontSize: '30px',
        fontWeight: '900',
      },
      (isMulti ? '임의합포건' : '송장미매칭') + dateBlock(trig ? barcode : result?.INB_TRS_INVOICE ?? ''),
      ' class="place"',
    )
  } else {
    head = el(
      'div',
      {
        width: '90%',
        marginBottom: '10px',
        textAlign: 'center',
        border: '1px solid black',
        fontSize: '28px',
        fontFamily: TREBUCHET,
        fontWeight: '900',
        color: 'white',
        backgroundColor: 'black',
      },
      escapeHtml(formatStorageZone(result)),
      ' class="place"',
    )
  }

  // 정보 줄 — 성공 일반·이형·재출력 라벨에만
  const info =
    !trig && normalLabel && !isMulti
      ? LABEL_INFO_KEYS.map((key) => {
          const prefix = KEY_PREFIX[key] ? `${KEY_PREFIX[key]} : ` : ''
          const text = result ? `${prefix}${String(result[key])}` : ''
          return el(
            'div',
            { width: '100%', textAlign: 'left', lineHeight: '1.4', fontFamily: TREBUCHET, fontWeight: '900' },
            `<span>${escapeHtml(text)}</span>`,
          )
        }).join('')
      : ''
  // 주문번호 — 성공 라벨이면 명령·임의 합포건 라벨에도 찍는다(정보 줄과 조건이 다르다). 실패 라벨에는 없다(LOGISPRD-14966).
  const orders = !trig && orderList.length > 0 ? orderBlock(orderList) : ''
  const star =
    result?.DANGER === 'Y' && !trig && (!isCommand || isReprint) ? el('div', { width: '40%', textAlign: 'right', fontSize: '50px' }, '★') : ''

  // ML번호·바코드 — 초기화·임의 합포건 라벨에는 없다
  let bottom = ''
  if (!isReset && !isMulti) {
    const ml = !isQty && result?.ORD_CONSOL_NO ? `ML번호 : ${result.ORD_CONSOL_NO}` : ''
    const value = trig ? barcode : isQty ? result?.INB_TRS_INVOICE ?? '' : result ? result.ORD_CONSOL_NO : ' '
    const width = result?.ORD_CONSOL_NO.includes('-') ? 1 : 1.3
    bottom =
      el(
        'div',
        { width: '100%', textAlign: isQty || trig ? 'center' : 'left', lineHeight: '1.2', fontFamily: TREBUCHET, fontWeight: '900' },
        `<span>${escapeHtml(ml)}</span>`,
      ) + el('div', { width: '100%', textAlign: 'center', paddingTop: '5px' }, barcodeSvg(value, width), ' class="barcode"')
  }

  const labelArea = el(
    'div',
    { width: '70%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0 auto' },
    head + el('div', { width: '100%', display: 'flex' }, el('div', { width: '100%' }, info + orders) + star) + bottom,
    ' class="labelArea"',
  )

  return el(
    'div',
    { display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '20px', width: '100%' },
    qrArea + labelArea,
    ' class="label_space"',
  )
}
